import { z } from 'zod';
import { esquemaConsultaPagina } from './comun.js';
/**
 * Contratos de carrito y órdenes.
 *
 * Del cliente se acepta SOLO qué quiere y a dónde. Ninguna cifra: los precios,
 * el flete, el IVA y el total los calcula el servidor. Si el esquema aceptara
 * un total, el cliente podría mandar el que quisiera.
 */
export const MAXIMO_LINEAS_ORDEN = 200;
/**
 * Una línea del pedido.
 *
 * Lleva el ARTÍCULO, no la oferta: el cliente no conoce las ofertas y no debe poder
 * nombrarlas. Cuál se compra lo decide el servidor al cotizar y, definitivamente, al
 * emitir —con los saldos ya bloqueados—. Aceptar un `productoId` del cliente le
 * permitiría elegir proveedor, que es exactamente lo que este modelo no hace.
 */
export const esquemaItemPedido = z.object({
    articuloId: z.string().min(1, 'Falta el artículo'),
    cantidad: z
        .number({ invalid_type_error: 'La cantidad debe ser un número' })
        .int('La cantidad debe ser un entero')
        .positive('La cantidad debe ser mayor que cero')
        .max(100_000, 'La cantidad es demasiado grande'),
});
export const esquemaCotizar = z
    .object({
    faenaId: z.string().min(1).nullish(),
    /*
     * El operador logístico NO se manda a cotizar, y es deliberado: hoy no mueve
     * ni un peso del cálculo —el tramo se sigue tarificando contra la zona de la
     * FAENA hasta que exista el tarifario del contrato— así que aceptarlo acá
     * sería un parámetro que el servidor recibe y descarta. El esquema es
     * `.strict()`, de modo que mandarlo da 400 y el frontend se entera de que
     * sobra en vez de creer que se está usando.
     *
     * Donde sí se exige y se valida es al EMITIR, que es cuando el despacho se
     * encarga de verdad.
     */
    items: z
        .array(esquemaItemPedido)
        .min(1, 'El carrito está vacío')
        .max(MAXIMO_LINEAS_ORDEN, `Máximo ${MAXIMO_LINEAS_ORDEN} líneas`),
})
    .strict();
export const esquemaEmitirOrden = z
    .object({
    faenaId: z.string().min(1, 'Falta la faena de destino'),
    /**
     * Operador logístico que retira y hace el último tramo hasta la faena.
     *
     * Opcional en el CONTRATO y obligatorio en la REGLA, y la diferencia no es
     * un descuido. Que sea obligatorio depende de un dato que solo el servidor
     * tiene —si el cliente tiene operadores asignados—, así que exigirlo acá
     * bloquearía a todo cliente que todavía no tiene ninguno. La comprobación
     * vive en el servicio de órdenes: con operadores asignados, falta uno es un
     * 400 que nombra el campo; sin ninguno, la orden sale con el tarifario
     * general como hasta ahora.
     */
    operadorLogisticoId: z.string().min(1).nullish(),
    items: z
        .array(esquemaItemPedido)
        .min(1, 'El carrito está vacío')
        .max(MAXIMO_LINEAS_ORDEN, `Máximo ${MAXIMO_LINEAS_ORDEN} líneas`),
    /** Marca las órdenes que necesitaron cotización manual. Alimenta el KPI. */
    requirioCotizacion: z.boolean().optional(),
})
    .strict();
export const ESTADOS_ORDEN_DTO = [
    'recibida',
    'confirmada',
    'en_preparacion',
    'despachada',
    /** Solo en el recorrido con bodega: el despacho directo no pasa por acá. */
    'recibido_en_bodega',
    'entregada',
    'cancelada',
];
export const esquemaCambiarEstado = z
    .object({
    estado: z.enum(ESTADOS_ORDEN_DTO),
    nota: z.string().max(500).optional(),
})
    .strict();
/**
 * Avance de UNA línea.
 *
 * `motivo` es obligatorio para cancelar y el servidor lo vuelve a exigir: acá se
 * pide como opcional porque el mismo esquema sirve para los cinco avances, y
 * hacerlo condicional en Zod produciría un mensaje peor que el del servidor.
 *
 * `cantidadDespachada` permite registrar un despacho parcial en el mismo paso en
 * que se marca la línea como despachada, que es cuando el proveedor tiene el
 * número a mano. Sin esto habría que hacer dos llamadas y la segunda se olvida.
 */
export const esquemaAvanzarLinea = z
    .object({
    estado: z.enum(ESTADOS_ORDEN_DTO),
    nota: z.string().max(500).optional(),
    motivo: z.string().trim().max(500).optional(),
    cantidadDespachada: z
        .number({ invalid_type_error: 'La cantidad despachada debe ser un número' })
        .int('La cantidad despachada debe ser un entero')
        .min(0, 'La cantidad despachada no puede ser negativa')
        .optional(),
})
    .strict();
export const APROBACIONES_CLIENTE_DTO = [
    'pendiente',
    'propuesta',
    'aprobada',
    'rechazada',
];
/**
 * Lo que el equipo le propone al cliente tras revisar su orden.
 *
 * Todo es opcional salvo la nota: aceptar la orden tal como llegó es el caso
 * frecuente y no debería obligar a repetir lo que ya está. Lo que viene, viene
 * como CAMBIO respecto de lo emitido.
 *
 * `lineas` solo lleva las que cambian. Mandar las 40 líneas de una orden para
 * tocar una obligaría al navegador a reconstruir el estado completo y a que el
 * servidor confíe en esa reconstrucción — que es justo lo que este sistema no
 * hace con nada que venga del cliente.
 */
export const esquemaProponerAjuste = z
    .object({
    /**
     * Qué se ajustó y por qué. Obligatoria: un cambio de precio o de fecha sin
     * explicación es una discusión por teléfono tres días después.
     */
    nota: z
        .string({ required_error: 'Explique qué ajustó y por qué' })
        .trim()
        .min(3, 'La nota es demasiado corta')
        .max(1000),
    /** Fecha de entrega propuesta, si se mueve la que calculó el motor. */
    fechaComprometida: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe ser AAAA-MM-DD')
        .optional(),
    lineas: z
        .array(z
        .object({
        lineaId: z.string().trim().length(36, 'La línea no es válida'),
        /** Cero no: para quitar una línea se cancela, y eso libera su stock. */
        cantidad: z
            .number()
            .int('La cantidad debe ser un entero')
            .min(1, 'Para dejar una línea en cero, cancélela')
            .optional(),
        /** Precio unitario neto en CLP. Cero es válido: una línea de regalo. */
        precioUnitario: z
            .number()
            .int('El precio debe ser un entero de pesos')
            .min(0, 'El precio no puede ser negativo')
            .optional(),
    })
        .strict()
        .refine((l) => l.cantidad !== undefined || l.precioUnitario !== undefined, {
        message: 'Una línea sin cantidad ni precio no es un ajuste',
    }))
        .max(200)
        .optional(),
})
    .strict();
/**
 * La respuesta del cliente a lo que se le propuso.
 *
 * Discriminada por la misma razón que la del proveedor: aprobar no lleva motivo
 * y rechazar no puede ir sin él. Un objeto con los dos campos opcionales dejaría
 * pasar un rechazo mudo, que es el que obliga a llamar por teléfono.
 */
export const esquemaResponderPropuesta = z.discriminatedUnion('respuesta', [
    z.object({ respuesta: z.literal('aprobada') }).strict(),
    z
        .object({
        respuesta: z.literal('rechazada'),
        motivo: z
            .string({ required_error: 'Indique qué no le acomoda' })
            .trim()
            .min(3, 'El motivo es demasiado corto')
            .max(1000),
    })
        .strict(),
]);
export const RESPUESTAS_PROVEEDOR_DTO = ['pendiente', 'aceptada', 'rechazada'];
/** Lo más largo que puede tener un motivo de rechazo. El mismo tope que la nota. */
export const LARGO_MAXIMO_MOTIVO = 500;
/**
 * La respuesta del proveedor a la OC que se le mandó.
 *
 * Se discrimina por `respuesta` porque los dos casos piden cosas distintas y
 * excluyentes: aceptar lleva una cantidad, rechazar lleva un motivo. Un solo
 * objeto con los dos campos opcionales dejaría pasar «rechazada con cantidad 8»,
 * que no significa nada, y obligaría al servidor a validar lo que el esquema
 * puede garantizar.
 *
 * `cantidadConfirmada` es opcional al aceptar: omitirla significa «acepto la
 * cantidad completa», que es la respuesta más frecuente y no debería costar un
 * campo. Cuando viene, puede ser MENOR a la pedida —«tengo 8 de las 10»— y el
 * servidor comprueba contra la línea que no sea mayor.
 */
export const esquemaResponderLinea = z.discriminatedUnion('respuesta', [
    z
        .object({
        respuesta: z.literal('aceptada'),
        cantidadConfirmada: z
            .number({ invalid_type_error: 'La cantidad confirmada debe ser un número' })
            .int('La cantidad confirmada debe ser un entero')
            .min(1, 'Para aceptar hay que confirmar al menos una unidad. Si no tiene, rechace.')
            .optional(),
    })
        .strict(),
    z
        .object({
        respuesta: z.literal('rechazada'),
        motivo: z
            .string({ required_error: 'Indique por qué no puede surtir esta línea' })
            .trim()
            .min(3, 'El motivo es demasiado corto')
            .max(LARGO_MAXIMO_MOTIVO),
    })
        .strict(),
]);
export const esquemaCancelar = z
    .object({
    motivo: z
        .string({ required_error: 'Indique el motivo de la cancelación' })
        .trim()
        .min(3, 'El motivo es demasiado corto')
        .max(500),
})
    .strict();
export const esquemaConsultaOrdenes = esquemaConsultaPagina.extend({
    estado: z.enum(ESTADOS_ORDEN_DTO).optional(),
});
/** Despacho parcial: lo registra el equipo, y es lo que alimenta el fill rate. */
export const esquemaDespachoLinea = z
    .object({
    lineas: z
        .array(z.object({
        lineaId: z.string().min(1),
        cantidadDespachada: z.number().int().min(0),
    }))
        .min(1),
})
    .strict();
//# sourceMappingURL=ordenes.js.map